// Utility functions for Minew Testing Tool

import type { Room } from './types';
import type { SensorModelType } from './constants';
import { SENSOR_TYPE_IDS } from './constants';

/**
 * Generate a test gateway ID for a room
 */
export const generateGatewayId = (room: Room): string => `GW_TEST_${room.type}_${room.id}`;

/**
 * Convert a number to a 2-digit hex string
 */
export const toHex = (num: number): string => ('0' + Number(num).toString(16)).slice(-2).toUpperCase();

/**
 * Format time as HH:MM
 */
export const formatTime = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

/**
 * Get today's date as YYYY-MM-DD
 */
export const getTodayDateString = (): string => new Date().toISOString().split('T')[0];

/**
 * Convert Fahrenheit to Celsius
 */
export const fahrenheitToCelsius = (f: number): number => ((f - 32) * 5) / 9;

/**
 * Convert a temperature (in Celsius) to 8.8 fixed-point hex string (4 chars)
 * Format: 16-bit signed integer representing value * 256
 */
export const temperatureToFixedPoint = (tempCelsius: number): string => {
  // Convert to 8.8 fixed-point: multiply by 256
  let fixedPoint = Math.round(tempCelsius * 256);

  // Handle negative values (two's complement for 16-bit)
  if (fixedPoint < 0) {
    fixedPoint = fixedPoint + 65536;
  }

  // Clamp to 16-bit range
  fixedPoint = Math.max(0, Math.min(65535, fixedPoint));

  return fixedPoint.toString(16).toUpperCase().padStart(4, '0');
};

/**
 * Convert humidity percentage to 8.8 fixed-point hex string (4 chars)
 * Format: 16-bit unsigned integer representing value * 256
 */
export const humidityToFixedPoint = (humidity: number): string => {
  // Convert to 8.8 fixed-point: multiply by 256
  const fixedPoint = Math.round(humidity * 256);

  // Clamp to 16-bit range
  const clamped = Math.max(0, Math.min(65535, fixedPoint));

  return clamped.toString(16).toUpperCase().padStart(4, '0');
};

/**
 * Generate sensor rawData payload for different sensor models
 * Format: 020106[len]FF3906CA[typeId:4][flags:2][battery:2][temp:4][humidity:4]...
 *
 * @param tempFahrenheit - Temperature in Fahrenheit
 * @param humidity - Humidity percentage (0-100)
 * @param battery - Battery level (0-100)
 * @param model - Sensor model type (FancyWhite, MST01, MST01V2)
 * @param hasHumidity - Whether sensor has humidity probe
 */
export const generateSensorRawData = (
  tempFahrenheit: number,
  humidity: number,
  battery: number,
  model: SensorModelType = 'MST01',
  hasHumidity: boolean = true
): string => {
  const tempCelsius = fahrenheitToCelsius(tempFahrenheit);
  const tempHex = temperatureToFixedPoint(tempCelsius);
  // Use 8000 (no humidity) if sensor doesn't have humidity probe
  const humidityHex = hasHumidity ? humidityToFixedPoint(humidity) : '8000';
  const batteryHex = toHex(battery);

  // Get sensor type ID from constants
  const typeId = SENSOR_TYPE_IDS[model];

  // Flags byte: 0x03 = normal, 0x01 = temperature only (if no humidity)
  // Based on Minew protocol: bit 0x02 indicates temperature-only sensor
  const flagsByte = hasHumidity ? '03' : '01';

  // Format: 020106[len]FF3906CA[typeId:4][flags:2][00][temp:4][humidity:4][model_name][padding]
  // Model name in ASCII (padded): MST01, FncyW, MST02
  const modelNames: Record<SensorModelType, string> = {
    MST01: '4D535430310000000000', // "MST01" + padding
    MST01V2: '4D5354303156320000', // "MST01V2" + padding
    FancyWhite: '466E63795768697465', // "FncyWhite" (truncated)
  };

  return `0201061BFF3906CA${typeId}${flagsByte}00${tempHex}${humidityHex}${modelNames[model]}D0079B17`;
};
